'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { CreditCard, ArrowLeft, Download, Eye, Search, CheckCircle2, Clock, AlertCircle, DollarSign, FileText, X,  } from 'lucide-react';
import Link from 'next/link';

interface Invoice {
  id: string;
  project: string;
  description: string;
  amount: string;
  amountNum: number;
  issued: string;
  due: string;
  status: 'Paid' | 'Pending' | 'Overdue';
  items: { label: string; qty: number; rate: string; total: string }[];
}

const invoices: Invoice[] = [
  {
    id: 'INV-2026-041',
    project: 'Nexus ERP Platform',
    description: 'QA & Testing Phase — Sprint 4',
    amount: '$8,400',
    amountNum: 8400,
    issued: 'Jul 25, 2026',
    due: 'Aug 20, 2026',
    status: 'Overdue',
    items: [
      { label: 'QA Engineering (160 hrs)', qty: 160, rate: '$45/hr', total: '$7,200' },
      { label: 'Test Environment Setup', qty: 1, rate: '$800', total: '$800' },
      { label: 'Bug Tracking & Reporting', qty: 1, rate: '$400', total: '$400' },
    ],
  },
  {
    id: 'INV-2026-038',
    project: 'BlueStar E-Commerce Portal',
    description: 'Backend Development — Milestone 3',
    amount: '$4,000',
    amountNum: 4000,
    issued: 'Aug 5, 2026',
    due: 'Sep 5, 2026',
    status: 'Pending',
    items: [
      { label: 'Backend API Development (80 hrs)', qty: 80, rate: '$40/hr', total: '$3,200' },
      { label: 'Database Schema Design', qty: 1, rate: '$500', total: '$500' },
      { label: 'Code Review & Documentation', qty: 1, rate: '$300', total: '$300' },
    ],
  },
  {
    id: 'INV-2026-031',
    project: 'Meridian Cloud Migration',
    description: 'Infrastructure Audit & Architecture Design',
    amount: '$6,200',
    amountNum: 6200,
    issued: 'Jul 1, 2026',
    due: 'Jul 30, 2026',
    status: 'Paid',
    items: [
      { label: 'Cloud Architecture Consulting (60 hrs)', qty: 60, rate: '$80/hr', total: '$4,800' },
      { label: 'Infrastructure Audit Report', qty: 1, rate: '$900', total: '$900' },
      { label: 'AWS Account Setup & Config', qty: 1, rate: '$500', total: '$500' },
    ],
  },
  {
    id: 'INV-2026-024',
    project: 'Nexus ERP Platform',
    description: 'Development Phase — Sprint 2 & 3',
    amount: '$21,000',
    amountNum: 21000,
    issued: 'Jun 1, 2026',
    due: 'Jun 25, 2026',
    status: 'Paid',
    items: [
      { label: 'Full-Stack Development (300 hrs)', qty: 300, rate: '$60/hr', total: '$18,000' },
      { label: 'UI/UX Implementation', qty: 1, rate: '$2,000', total: '$2,000' },
      { label: 'API Integration', qty: 1, rate: '$1,000', total: '$1,000' },
    ],
  },
  {
    id: 'INV-2026-017',
    project: 'BlueStar E-Commerce Portal',
    description: 'Design & Frontend — Milestone 1 & 2',
    amount: '$9,500',
    amountNum: 9500,
    issued: 'May 20, 2026',
    due: 'Jun 15, 2026',
    status: 'Paid',
    items: [
      { label: 'UI/UX Design (80 hrs)', qty: 80, rate: '$75/hr', total: '$6,000' },
      { label: 'Frontend Development (70 hrs)', qty: 70, rate: '$50/hr', total: '$3,500' },
    ],
  },
];

const statusConfig = {
  Paid: { color: 'text-success', bg: 'bg-success/10', icon: CheckCircle2 },
  Pending: { color: 'text-warning', bg: 'bg-warning/10', icon: Clock },
  Overdue: { color: 'text-danger', bg: 'bg-danger/10', icon: AlertCircle },
};

export default function ClientInvoicesPage() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('All');
  const [viewInvoice, setViewInvoice] = useState<Invoice | null>(null);

  const totalPaid = invoices.filter((i) => i.status === 'Paid').reduce((s, i) => s + i.amountNum, 0);
  const totalPending = invoices.filter((i) => i.status !== 'Paid').reduce((s, i) => s + i.amountNum, 0);

  const filtered = invoices.filter((inv) => {
    const matchSearch = inv.id.toLowerCase().includes(search.toLowerCase()) || inv.project.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || inv.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <AppLayout>
      <div className="min-h-screen bg-background px-6 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link href="/client-portal">
            <button className="p-2 rounded-lg border border-border hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
              <ArrowLeft size={16} />
            </button>
          </Link>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Client Portal</p>
            <h1 className="text-xl font-bold text-foreground">Invoices & Payments</h1>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={15} className="text-success" />
              <span className="text-xs text-muted-foreground">Total Paid</span>
            </div>
            <p className="text-metric text-foreground">${totalPaid.toLocaleString()}</p>
          </div>
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={15} className="text-warning" />
              <span className="text-xs text-muted-foreground">Outstanding</span>
            </div>
            <p className="text-metric text-foreground">${totalPending.toLocaleString()}</p>
          </div>
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 size={15} className="text-success" />
              <span className="text-xs text-muted-foreground">Paid Invoices</span>
            </div>
            <p className="text-metric text-foreground">{invoices.filter((i) => i.status === 'Paid').length}</p>
          </div>
          <div className="card-surface p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={15} className="text-danger" />
              <span className="text-xs text-muted-foreground">Overdue</span>
            </div>
            <p className="text-metric text-foreground">{invoices.filter((i) => i.status === 'Overdue').length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invoices..."
              className="w-full bg-card border border-border rounded-lg pl-8 pr-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          <div className="flex items-center gap-2">
            {['All', 'Paid', 'Pending', 'Overdue'].map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Invoice Table */}
        <div className="card-surface overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Invoice</th>
                <th className="text-left px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Project</th>
                <th className="text-left px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Issued</th>
                <th className="text-left px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground hidden md:table-cell">Due Date</th>
                <th className="text-right px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Amount</th>
                <th className="text-center px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="text-center px-4 py-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv, idx) => {
                const cfg = statusConfig[inv.status];
                const StatusIcon = cfg.icon;
                return (
                  <tr key={inv.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${idx % 2 === 0 ? '' : 'bg-muted/5'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-mono text-foreground">{inv.id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{inv.project}</p>
                      <p className="text-2xs text-muted-foreground">{inv.description}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">{inv.issued}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">{inv.due}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-sm font-semibold text-foreground">{inv.amount}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon size={11} />
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setViewInvoice(inv)}
                          className="p-1.5 rounded-md hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground"
                          title="View Invoice"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded-md hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground"
                          title="Download PDF"
                        >
                          <Download size={14} />
                        </button>
                        {inv.status !== 'Paid' && (
                          <button className="px-2.5 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium hover:bg-primary/20 transition-colors">
                            Pay Now
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-muted-foreground text-sm">No invoices found.</div>
          )}
        </div>

        {/* Invoice Detail Modal */}
        {viewInvoice && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="card-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-thin">
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div>
                  <h2 className="text-base font-bold text-foreground">{viewInvoice.id}</h2>
                  <p className="text-xs text-muted-foreground">{viewInvoice.project}</p>
                </div>
                <button onClick={() => setViewInvoice(null)} className="p-2 rounded-lg hover:bg-muted/40 transition-colors text-muted-foreground hover:text-foreground">
                  <X size={16} />
                </button>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-2 gap-4 mb-5 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Issued</p>
                    <p className="text-foreground font-medium">{viewInvoice.issued}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Due Date</p>
                    <p className="text-foreground font-medium">{viewInvoice.due}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Status</p>
                    <span className={`text-xs font-medium ${statusConfig[viewInvoice.status].color}`}>{viewInvoice.status}</span>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs mb-0.5">Description</p>
                    <p className="text-foreground font-medium text-xs">{viewInvoice.description}</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg overflow-hidden mb-5">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/30 border-b border-border">
                        <th className="text-left px-3 py-2 text-xs text-muted-foreground font-medium">Item</th>
                        <th className="text-right px-3 py-2 text-xs text-muted-foreground font-medium">Qty</th>
                        <th className="text-right px-3 py-2 text-xs text-muted-foreground font-medium">Rate</th>
                        <th className="text-right px-3 py-2 text-xs text-muted-foreground font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewInvoice.items.map((item, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="px-3 py-2 text-foreground">{item.label}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{item.qty}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{item.rate}</td>
                          <td className="px-3 py-2 text-right text-foreground font-medium">{item.total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/20">
                        <td colSpan={3} className="px-3 py-2 text-right text-sm font-semibold text-foreground">Total</td>
                        <td className="px-3 py-2 text-right text-sm font-bold text-primary">{viewInvoice.amount}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="flex gap-3">
                  <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/40 transition-colors">
                    <Download size={14} /> Download PDF
                  </button>
                  {viewInvoice.status !== 'Paid' && (
                    <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                      <CreditCard size={14} /> Pay Now
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
